import React, { useContext, useState } from 'react'
import { Alert, Box, BoxProps, Button, Flex, FormControl, Select, Text, Spinner } from '@chakra-ui/react'

import { FormLabel } from '../components/FormLabel'
import { Animal, Assistance, Cloud, Elements, Injury, Thief, Windows } from '../icons'
import { Card, CardBody } from '../components/Card'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { useGetRiskVariants } from '../hooks'
import { Risk } from 'BrokerTsLib/src/enums'
import { insuranceParamsContext } from '../layouts/Wizard'
import { Addon, InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { useCebiGlass } from '../hooks/useVehicle'
import { Loading } from '../components/Loading'
import { useApp } from '../hooks/useApp'

const enum ExtraInsuranceCarItemIconsEnum {
  Assistance = 'assistance',
  Windows = 'windows',
  Injury = 'injury',
  Elements = 'elements',
  Animal = 'animal',
  Thief = 'thief',
}

interface GlassResultProps {
  title: string
  price: number
  buttonLabel?: string
  onInsertValue: (price: number) => void
}

const getExtraInsuranceCarIcon = (icon: ExtraInsuranceCarItemIconsEnum) => {
  const size: string = '48px'

  switch (icon) {
    case 'assistance':
      return <Assistance width={size} height={size} />
    case 'windows':
      return <Windows width={size} height={size} />
    case 'injury':
      return <Injury width={size} height={size} />
    case 'elements':
      return <Elements width={size} height={size} />
    case 'animal':
      return <Animal width={size} height={size} />
    case 'thief':
      return <Thief width={size} height={size} />
  }
}

const ExtraInsuranceGroup = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ w: '100%', display: 'grid', alignContent: 'start', rowGap: 8 }}>{children}</Box>
}

const ExtraInsuranceItem = ({
  children,
  label,
  tooltip,
  icon,
  loading,
  sx,
}: {
  children: React.ReactNode
  label: string
  tooltip?: string
  icon: ExtraInsuranceCarItemIconsEnum
  loading: boolean
  sx?: BoxProps['sx']
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, ...sx }}>
      {icon && <Box sx={{ color: 'secondary.500' }}>{getExtraInsuranceCarIcon(icon)}</Box>}
      <FormControl sx={{}}>
        <FormLabel tooltip={tooltip}>{label}</FormLabel>
        {loading && <Spinner />}
        {children}
      </FormControl>
    </Box>
  )
}

const GlassResult = ({ title, price, buttonLabel, onInsertValue }: GlassResultProps) => {
  return (
    <Box
      sx={{
        display: 'grid',
        w: '100%',
        p: 2,
        gap: 2,
        placeContent: 'center',
        placeItems: 'center',
        bg: 'gray.50',
        borderRadius: 'base',
      }}
    >
      <Box sx={{ display: 'grid', placeContent: 'center', placeItems: 'center', gap: 0 }}>
        <Text as="p">{title}</Text>

        <Text as="p">
          <strong>{formatPrice({ price })} včetně DPH</strong>
        </Text>
      </Box>

      {buttonLabel && (
        <Button variant="outline" size="md" onClick={() => onInsertValue(price)}>
          {buttonLabel}
        </Button>
      )}
    </Box>
  )
}

type ExtraInsuranceCarProps = {
  addons: Addon[]
  changeAddon: (addon: Addon) => void
}

export const ExtraInsuranceCar = ({ addons, changeAddon }: ExtraInsuranceCarProps) => {
  const { environment } = useApp()
  const { insuranceParams, isLoadingData } = useContext(insuranceParamsContext)
  const { vin, licensePlate } = (insuranceParams as InsuranceParamsDto)?.vehicle ?? {};
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const assistance = addons?.find(x => x.riskId == Risk.ASISTENCE)?.riskVariantId?.toString()
  const [glass, setGlass] = useState(addons?.find(x => x.riskId == Risk.SKLA)?.riskVariantId?.toString())
  const sedadla = addons?.find(x => x.riskId == Risk.SEDADLA)?.riskVariantId?.toString()
  const zivel = addons?.find(x => x.riskId == Risk.ZIVEL)?.riskVariantId?.toString()
  const zver = addons?.find(x => x.riskId == Risk.ZVER)?.riskVariantId?.toString()
  const odcpov = addons?.find(x => x.riskId == Risk.ODCPOV)?.riskVariantId?.toString()

  const directLiquidation = addons?.find(x => x.riskId == Risk.DirectLiquidation)?.riskVariantId?.toString()
  const nahr = addons?.find(x => x.riskId == Risk.NAHR)?.riskVariantId?.toString()

  const { data: dataAssistance, isLoading: isLoadingAssistance } = useGetRiskVariants(Risk.ASISTENCE)
  const { data: dataGlass, isLoading: isLoadingGlass } = useGetRiskVariants(Risk.SKLA)
  const { data: dataSedadla, isLoading: isLoadingSedadla } = useGetRiskVariants(Risk.SEDADLA)
  const { data: dataZivel, isLoading: isLoadingZivel } = useGetRiskVariants(Risk.ZIVEL)
  const { data: dataZver, isLoading: isLoadingZver } = useGetRiskVariants(Risk.ZVER)
  const { data: dataOdcpov, isLoading: isLoadingOdcpov } = useGetRiskVariants(Risk.ODCPOV)

  const { data: dataDirectLiquidation, isLoading: isLoadingDirectLiquidation } = useGetRiskVariants(Risk.DirectLiquidation)
  const { data: dataNahr, isLoading: isLoadingNahr } = useGetRiskVariants(Risk.NAHR)

  const isLoading = isLoadingAssistance || isLoadingGlass

  const glassValues = dataGlass?.data ?? [];
  const { data: cebiGlass, mutateAsync: getCebiGlass, isLoading: isLoadingCebiGlass } = useCebiGlass()
  const [showValuations, setShowValuations] = useState(false);

  const _toAddon = (value: string, risk: Risk): Addon => {
    var riskVariantId = parseInt(value)

    return {
      riskId: parseInt(risk.toString()),
      riskVariantId: riskVariantId == 0 || !!riskVariantId ? riskVariantId : undefined
    }
  }

  const selectGlassValue = (value: number) => {
    var values = glassValues.filter(x => !!x.value).map(x => ({ value: parseInt(x.value!), id: x.id })) ?? []
    if (values.length == 0) return

    // prvni stejny nebo vetsi
    let selected = undefined
    var filtered = values.filter(x => x.value >= value).sort((a, b) => a.value - b.value) ?? []
    if (filtered.length > 0)
      selected = filtered[0].id

    // prvni mensi
    if (!selected) {
      filtered = values.filter(x => x.value < value).sort((a, b) => b.value - a.value) ?? []
      if (filtered.length > 0)
        selected = filtered[0].id
    }

    if (selected) {
      setGlass(selected.toString())
      changeAddon(_toAddon(selected.toString(), Risk.SKLA))
      setShowValuations(false)
    }
  }

  return (
    <Box
      sx={{
        w: '100%',
        display: 'grid',
        gridTemplateColumns: { base: '1fr', lg: '1fr 1fr' },
        gap: 8,
      }}
    >


      <ExtraInsuranceGroup>

        {isLoading && <Loading large={true} />}

        {dataAssistance?.data && !isLoadingAssistance &&
          <ExtraInsuranceItem label="Příplatková asistence"
            tooltip="Vyberte typ asistence, která bude přidána do všech nabídek na dalším kroku."
            icon={ExtraInsuranceCarItemIconsEnum.Assistance} loading={isLoadingAssistance}>
            <Select defaultValue={assistance} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.ASISTENCE)))} isDisabled={isContract}>
              {dataAssistance?.data && dataAssistance.data.map(x => (
                <option value={x.id.toString()} key={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </ExtraInsuranceItem>
        }

        {glassValues.length > 0 && !isLoadingGlass &&
          <Box sx={{ display: 'grid', gap: 8 }}>
            <Flex sx={{ gap: 4, alignItems: 'end' }}>
              <ExtraInsuranceItem label="Skla"
                tooltip="Zadejte limit na pojištění všech skel. V případě, že si nejste jistí výběrem limitu, můžete sklo ocenit. Výsledná cena ocenění je za materiál včetně práce za výměnu/opravu."
                icon={ExtraInsuranceCarItemIconsEnum.Windows} loading={isLoadingGlass}>
                <Select value={glass} onChange={e => {
                  setGlass(e.target.value)
                  changeAddon(_toAddon(e.target.value, Risk.SKLA))
                }}
                  isDisabled={isContract}
                >
                  {glassValues.map(x => (
                    <option value={x.id.toString()} key={x.id}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </ExtraInsuranceItem>

              {!isLoadingData && <Button
                variant="outline"
                isDisabled={isContract}
                isLoading={isLoadingCebiGlass}
                onClick={() => getCebiGlass({ licencePlate: licensePlate, vin: vin }).then(x => setShowValuations(true))}>
                <Cloud color="secondary.500" />
                Ocenit skla
              </Button>}
            </Flex>

            {(cebiGlass?.data && !isLoadingCebiGlass && showValuations &&
              <Card>
                <CardBody sx={{ p: 4 }}>
                  <Text as="p" sx={{ mb: 2, fontWeight: '600' }}>
                    Ocenění čelního skla
                  </Text>

                  <Text variant="textMuted" sx={{ mb: 4, fontSize: 'sm' }}>
                    VIN: {vin}
                  </Text>

                  {!cebiGlass.data.error &&
                    <Flex sx={{ gap: 4 }}>
                      {cebiGlass.data.PriceOriginal && cebiGlass.data.PriceOriginal > 0 && (
                        <GlassResult
                          title="Nové"
                          price={cebiGlass.data.PriceOriginal}
                          buttonLabel="Vložit nové"
                          onInsertValue={selectGlassValue} />
                      )}

                      {cebiGlass.data.PriceSecondaryManufacturing && cebiGlass.data.PriceSecondaryManufacturing > 0 && (
                        <GlassResult
                          title="Druhovýroba"
                          price={cebiGlass.data.PriceSecondaryManufacturing}
                          buttonLabel="Vložit druhovýrobu"
                          onInsertValue={selectGlassValue}
                        />
                      )}
                    </Flex>
                  }

                  {cebiGlass.data.error && <Alert colorScheme="red">Chyba: {cebiGlass.data.error}</Alert>}
                </CardBody>
              </Card>
            )}
          </Box>

        }


        {/* {dataDirectLiquidation?.data && !isLoadingDirectLiquidation &&
              <ExtraInsuranceItem label="Přímá likvidace"
                tooltip="Přímá likvidace."
                icon={ExtraInsuranceCarItemIconsEnum.Elements} loading={isLoadingDirectLiquidation}>
                <Select defaultValue={directLiquidation} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.DirectLiquidation)))} isDisabled={isContract}>
                  {dataDirectLiquidation?.data && dataDirectLiquidation.data.map(x => (
                    <option value={x.id.toString()} key={x.id}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </ExtraInsuranceItem>
            }

            {dataNahr?.data && !isLoadingNahr &&
              <ExtraInsuranceItem label="Náhradní vozidlo"
                tooltip="Pojištění nákladů na nájem náhradního vozidla."
                icon={ExtraInsuranceCarItemIconsEnum.Assistance} loading={isLoadingNahr}>
                <Select defaultValue={nahr} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.NAHR)))} isDisabled={isContract}>
                  {dataNahr?.data && dataNahr.data.map(x => (
                    <option value={x.id.toString()} key={x.id}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </ExtraInsuranceItem>
            } */}

        {dataSedadla?.data && !isLoadingSedadla &&
          <ExtraInsuranceItem label="Úraz osob"
            tooltip="Úrazové pojištění."
            icon={ExtraInsuranceCarItemIconsEnum.Injury} loading={isLoadingSedadla}>
            <Select defaultValue={sedadla} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.SEDADLA)))} isDisabled={isContract}>
              {dataSedadla?.data && dataSedadla.data.map(x => (
                <option value={x.id.toString()} key={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </ExtraInsuranceItem>
        }
      </ExtraInsuranceGroup>

      <ExtraInsuranceGroup>
        {dataZivel?.data && !isLoadingZivel &&
          <ExtraInsuranceItem label="Živel"
            tooltip="Pojištění pro případ živelní škody."
            icon={ExtraInsuranceCarItemIconsEnum.Elements} loading={isLoadingZivel}>
            <Select defaultValue={zivel} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.ZIVEL)))} isDisabled={isContract}>
              {dataZivel?.data && dataZivel.data.map(x => (
                <option value={x.id.toString()} key={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </ExtraInsuranceItem>
        }

        {dataZver?.data && !isLoadingZver &&
          <ExtraInsuranceItem label="Srážka se zvěří"
            tooltip="Pojištění pro případ srážky se zvěří."
            icon={ExtraInsuranceCarItemIconsEnum.Animal} loading={isLoadingZver}>
            <Select defaultValue={zver} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.ZVER)))} isDisabled={isContract}>
              {dataZver?.data && dataZver.data.map(x => (
                <option value={x.id.toString()} key={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </ExtraInsuranceItem>
        }

        {dataOdcpov?.data && !isLoadingOdcpov &&
          <ExtraInsuranceItem label="Odcizení celého vozidla"
            tooltip="Pojištění pro případ odcizení celého vozidla."
            icon={ExtraInsuranceCarItemIconsEnum.Thief} loading={isLoadingOdcpov}>
            <Select defaultValue={odcpov} onChange={(e => changeAddon(_toAddon(e.target.value, Risk.ODCPOV)))} isDisabled={isContract}>
              {dataOdcpov?.data && dataOdcpov.data.map(x => (
                <option value={x.id.toString()} key={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </ExtraInsuranceItem>
        }

      </ExtraInsuranceGroup >

    </Box >
  )
}
